/**
 * Cash & Accounts — the operator's live view of the chart of accounts and each
 * account's balance (UPGRADE-01). Read-gated on `ledger.read`. Balances come
 * straight from the general ledger (Σ debit − credit, sign-normalised per
 * account type), so this is the single place to see the cash position across
 * Main Cash, bank, and mobile-money floats as payments post to the ledger.
 */
import { useQuery } from "@tanstack/react-query";
import { Badge } from "../components/Badge.js";
import { CashMovementsPanel } from "../components/CashMovementsPanel.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { Table, TBody, Td, Th, THead, Tr } from "../components/Table.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { formatMoney } from "../lib/utils.js";

interface AccountView {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly type: string;
  readonly category: string;
  readonly ownerType: string;
  readonly ownerId: string | null;
  readonly currency: string;
  readonly isSystem: boolean;
  readonly isActive: boolean;
  readonly balanceMinor: number;
}

const CATEGORY_LABELS: Record<string, string> = {
  cash_on_hand: "Cash",
  bank: "Bank",
  mobile_money: "Mobile money",
  stock: "Stock",
  receivable: "Receivables",
  payable: "Payables",
  income: "Income",
  expense: "Expenses",
  equity: "Equity",
  suspense: "Clearing",
};

// Order categories so the treasury-relevant ones lead.
const CATEGORY_ORDER = [
  "cash_on_hand",
  "bank",
  "mobile_money",
  "stock",
  "receivable",
  "payable",
  "income",
  "expense",
  "equity",
  "suspense",
];

const CASH_CATEGORIES = new Set(["cash_on_hand", "bank", "mobile_money"]);

export function CashPage(): JSX.Element {
  const auth = useAuth();
  const canRead = (auth.user?.permissions ?? []).includes("ledger.read");

  const q = useQuery({
    queryKey: ["accounting", "accounts"],
    queryFn: () => api.get<readonly AccountView[]>("/api/accounting/accounts"),
    enabled: canRead,
  });

  if (!canRead) {
    return (
      <div className="p-6 text-slate-600">
        You do not have access to accounting (requires the <code>ledger.read</code> permission).
      </div>
    );
  }

  const accounts = q.data ?? [];
  const currency = accounts[0]?.currency ?? "TZS";
  const cashPosition = accounts
    .filter((a) => CASH_CATEGORIES.has(a.category) && a.isActive)
    .reduce((sum, a) => sum + a.balanceMinor, 0);

  const byCategory = new Map<string, AccountView[]>();
  for (const a of accounts) {
    const arr = byCategory.get(a.category) ?? [];
    arr.push(a);
    byCategory.set(a.category, arr);
  }
  const categories = [...byCategory.keys()].sort(
    (a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b),
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">Cash &amp; Accounts</h1>
        <p className="text-sm text-slate-500">Live balances from the general ledger.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Total cash position</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="text-2xl font-semibold text-slate-800 tabular-nums">
            {formatMoney(cashPosition, currency)}
          </div>
          <div className="text-xs text-slate-500">
            Sum of active cash, bank, and mobile-money accounts.
          </div>
        </CardBody>
      </Card>

      {q.isLoading ? <div className="text-slate-500">Loading accounts…</div> : null}
      {q.isError ? <div className="text-rose-600">Failed to load accounts.</div> : null}

      {categories.map((cat) => (
        <Card key={cat}>
          <CardHeader>
            <CardTitle>{CATEGORY_LABELS[cat] ?? cat}</CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <Table>
              <THead>
                <Tr>
                  <Th>Account</Th>
                  <Th>Code</Th>
                  <Th>Type</Th>
                  <Th className="text-right">Balance</Th>
                </Tr>
              </THead>
              <TBody>
                {(byCategory.get(cat) ?? []).map((a) => (
                  <Tr key={a.id}>
                    <Td>
                      {a.name}
                      {a.isSystem ? (
                        <Badge tone="neutral" className="ml-2">
                          system
                        </Badge>
                      ) : null}
                      {!a.isActive ? (
                        <Badge tone="warning" className="ml-2">
                          inactive
                        </Badge>
                      ) : null}
                    </Td>
                    <Td className="text-slate-500">{a.code}</Td>
                    <Td className="text-slate-500">{a.type}</Td>
                    <Td className="text-right tabular-nums">
                      {formatMoney(a.balanceMinor, a.currency)}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </CardBody>
        </Card>
      ))}

      <CashMovementsPanel />
    </div>
  );
}
